import { useMemo, useState } from "react";
import cn from "classnames";
import { ValidityDateRangeFields } from "components/ui/DayPicker";
import { Select } from "components/ui/Select";
import { CARRIER_SELECT_OPTIONS, CARRIER_TBN_CODE, resolveCarrier } from "constants/carriers.catalog";
import { AnchorIcon, LinkIcon, PlaneIcon, TruckIcon } from "./RateIcons";
import type { LinerTermChoice, RateRecord } from "./rates.model";
import {
  applyLinerTerm,
  applyRateEdit,
  BUCKET_LABEL,
  carrierInitials,
  EQUIPMENT,
  formatMoney,
  formatTransitLabel,
  formatValidityLabel,
  LINER_TERM_OPTIONS,
  linerTermFromRate,
  linerTermLabels,
  MODE_OPTIONS,
  parseTransitDays,
  parseValidityRange,
  sellingAmount,
  SERVICE_OPTIONS,
  UNIT_OPTIONS,
} from "./rates.model";

type RateCardProps = {
  rate: RateRecord;
  variant: "library" | "publish" | "overview";
  onEdit?: (rate: RateRecord) => void;
  onDelete?: (id: string) => void;
};

const EndIcon = ({ mode }: { mode: RateRecord["mode"] }) => {
  if (mode === "air") return <PlaneIcon />;
  if (mode === "road") return <TruckIcon />;

  return <AnchorIcon />;
};

const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "CNY", "AED", "JPY"] as const;

/** Normalize 40HC / 40 HC / 40' HC → 40'HC for select matching. */
const normalizeEquipment = (value: string): string => {
  const text = value.trim();
  if (!text || text === "—" || text === "-" || /^not stated$/i.test(text)) return "";

  const compact = text.replace(/[\s'′]/g, "").toUpperCase();
  const match = EQUIPMENT.find((item) => item.replace(/'/g, "").toUpperCase() === compact);
  return match ?? text;
};

const equipmentSelectValue = (value: string): string => normalizeEquipment(value);

const unitSelectValue = (value: string): string => {
  if (UNIT_OPTIONS.some((option) => option.value === value)) return value;
  if (!value.trim() || value === "—") return "";

  return value;
};

export const RateCard = ({ rate, variant, onEdit, onDelete }: RateCardProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rate);
  const shown = editing ? applyRateEdit(rate, draft) : rate;
  const validityRange = parseValidityRange(draft.validity);
  const invalidValidityRange =
    Boolean(validityRange.from && validityRange.until) && validityRange.until < validityRange.from;
  const transitDays = parseTransitDays(draft.transit);
  const transitRaw = draft.transit.trim();
  const transitInvalid = transitRaw !== "" && transitRaw !== "—" && (transitDays == null || transitDays < 1);
  const saveDisabled = invalidValidityRange || transitInvalid;
  const currencyOptions =
    draft.currency && !(CURRENCY_OPTIONS as readonly string[]).includes(draft.currency)
      ? [draft.currency, ...CURRENCY_OPTIONS]
      : [...CURRENCY_OPTIONS];
  const equipmentValue = equipmentSelectValue(draft.equipment);
  const equipmentOptions: string[] = EQUIPMENT.includes(equipmentValue as (typeof EQUIPMENT)[number])
    ? [...EQUIPMENT]
    : equipmentValue
      ? [equipmentValue, ...EQUIPMENT]
      : [...EQUIPMENT];
  const unitValue = unitSelectValue(draft.unit);
  const unitOptions =
    unitValue && !UNIT_OPTIONS.some((option) => option.value === unitValue)
      ? [{ value: unitValue, label: unitValue }, ...UNIT_OPTIONS]
      : [...UNIT_OPTIONS];
  const linerChoice = linerTermFromRate(draft);
  const linerLabels = linerTermLabels(linerTermFromRate(shown));
  const resolvedCarrier = useMemo(
    () => resolveCarrier(draft.carrierCode || draft.carrier),
    [draft.carrier, draft.carrierCode]
  );
  const carrierSelectValue = resolvedCarrier.code || CARRIER_TBN_CODE;
  const carrierSelectOptions = useMemo(() => {
    if (CARRIER_SELECT_OPTIONS.some((option) => option.value === carrierSelectValue)) {
      return CARRIER_SELECT_OPTIONS;
    }
    return [{ value: carrierSelectValue, label: draft.carrier || carrierSelectValue }, ...CARRIER_SELECT_OPTIONS];
  }, [carrierSelectValue, draft.carrier]);

  const stCls = shown.status === "ready" ? "rt-st--ok" : shown.status === "review" ? "rt-st--warn" : "rt-st--con";
  const price = shown.amount === null ? "—" : formatMoney(shown.currency, shown.amount);
  const unit =
    shown.unit === "container" && shown.equipment && shown.equipment !== "—" && shown.equipment !== "" ? (
      <>
        per <span className="rt-eq">{shown.equipment}</span> cntr
      </>
    ) : shown.unit === "" || shown.unit === "—" ? (
      "—"
    ) : (
      shown.unit
    );

  const setValidityDates = (from: string, until: string) => {
    const nextValidity =
      !from && !until
        ? "Not stated"
        : from && until
          ? formatValidityLabel(from, until)
          : formatValidityLabel(from || until, from || until);

    setDraft({ ...draft, validity: nextValidity });
  };

  const setLinerTerm = (choice: LinerTermChoice) => {
    setDraft(applyLinerTerm(draft, choice));
  };

  const save = () => {
    if (saveDisabled) return;
    onEdit?.(applyRateEdit(rate, draft));
    setEditing(false);
  };

  return (
    <article
      className={cn("rt-card", `rt-card--${shown.status}`, { "is-editing": editing })}
      data-mode={shown.mode}
      data-state={shown.status}
    >
      <div className="rt-card__hd">
        <span className="rt-card__logo rt-card__logo--mark" aria-hidden="true">
          {carrierInitials(shown.carrier)}
        </span>
        <b className="rt-card__car">{shown.carrier}</b>
        {variant !== "overview" && (
          <span className={cn("rt-st", stCls, "rt-card__st")}>{BUCKET_LABEL[shown.status]}</span>
        )}
        <span className="rt-card__id">
          <LinkIcon />
          ID: {shown.id}
        </span>
      </div>
      <div className="rt-card__b">
        <div className="rt-card__route">
          <div className="rt-card__ends">
            <b title={shown.origin}>{shown.origin}</b>
            <b title={shown.destination}>{shown.destination}</b>
          </div>
          <div className="rt-card__transit">{shown.transit !== "—" ? shown.transit : "Transit —"}</div>
          <div className="rt-card__line">
            <i>
              <EndIcon mode={shown.mode} />
            </i>
            <span />
            <i>
              <EndIcon mode={shown.mode} />
            </i>
          </div>
          <p className="rt-card__ports">
            {linerLabels.title}
            <em>{linerLabels.subtitle}</em>
          </p>
          {shown.issues.length > 0 && (
            <div className="rt-why">
              {shown.issues.map((issue) => (
                <span key={issue} className={cn("rt-flag", { "rt-flag--bad": shown.status === "rejected" })}>
                  {issue}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="rt-card__r">
          {variant === "publish" && shown.amount !== null ? (
            <div className="rt-card__price rt-card__price--sell">
              <span className="rt-pr">
                <em>Carrier rate</em>
                <b>{price}</b>
              </span>
              <span className="rt-pr rt-pr--sell">
                <em>Selling rate · +10%</em>
                <b>{formatMoney(shown.currency, sellingAmount(shown.amount))}</b>
              </span>
              <em className="rt-card__unit">{unit}</em>
            </div>
          ) : (
            <div className="rt-card__price">
              <b>{price}</b>
              <em>{unit}</em>
            </div>
          )}
          <div className="rt-card__val">
            <span>Validity</span>
            <b>{shown.validity}</b>
          </div>
          {variant !== "overview" && (
            <div className="rt-c__btns">
              {variant === "library" && shown.status !== "rejected" && (
                <button
                  type="button"
                  className="pcbtn"
                  onClick={() => {
                    const resolved = resolveCarrier(rate.carrierCode || rate.carrier);
                    setDraft({
                      ...rate,
                      carrier: resolved.name,
                      carrierCode: resolved.code,
                    });
                    setEditing((open) => !open);
                  }}
                >
                  {editing ? "Cancel" : "Edit"}
                </button>
              )}
              <button type="button" className="pcbtn" onClick={() => onDelete?.(rate.id)}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
      {editing && (
        <div className="rt-ed">
          <div className="rt-ed__grid">
            <label className="rt-ed__f">
              <span>Carrier</span>
              <Select
                searchable
                ariaLabel="Carrier"
                placeholder="Carrier TBN"
                value={carrierSelectValue}
                options={carrierSelectOptions}
                onChange={(code) => {
                  const resolved = resolveCarrier(code);
                  setDraft({
                    ...draft,
                    carrier: resolved.name,
                    carrierCode: resolved.code,
                  });
                }}
              />
            </label>
            <label className="rt-ed__f">
              <span>Rate</span>
              <div className="rt-ed__amount">
                <input
                  value={draft.amount ?? ""}
                  inputMode="decimal"
                  onChange={(event) =>
                    setDraft({ ...draft, amount: event.target.value === "" ? null : Number(event.target.value) })
                  }
                />
                <Select
                  ariaLabel="Currency"
                  placeholder="—"
                  value={draft.currency || ""}
                  options={currencyOptions.map((code) => ({ value: code, label: code }))}
                  onChange={(code) => setDraft({ ...draft, currency: code })}
                />
              </div>
            </label>
            <label className="rt-ed__f">
              <span>Origin</span>
              <input value={draft.origin} onChange={(event) => setDraft({ ...draft, origin: event.target.value })} />
            </label>
            <label className="rt-ed__f">
              <span>Destination</span>
              <input
                value={draft.destination}
                onChange={(event) => setDraft({ ...draft, destination: event.target.value })}
              />
            </label>
            <label className="rt-ed__f">
              <span>Container type</span>
              <Select
                ariaLabel="Container type"
                placeholder="Select type"
                value={equipmentValue}
                options={equipmentOptions.map((item) => ({ value: item, label: item }))}
                onChange={(value) => setDraft({ ...draft, equipment: value })}
              />
            </label>
            <label className="rt-ed__f">
              <span>Unit</span>
              <Select
                ariaLabel="Unit"
                placeholder="—"
                value={unitValue}
                options={unitOptions.map((option) => ({ value: option.value, label: option.label }))}
                onChange={(value) => setDraft({ ...draft, unit: value || "—" })}
              />
            </label>
            <label className="rt-ed__f">
              <span>Service</span>
              <Select
                ariaLabel="Service"
                value={draft.service}
                options={SERVICE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                onChange={(value) => setDraft({ ...draft, service: value as RateRecord["service"] })}
              />
            </label>
            <label className="rt-ed__f">
              <span>Mode</span>
              <Select
                ariaLabel="Mode"
                value={draft.mode}
                options={MODE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                onChange={(value) => setDraft({ ...draft, mode: value as RateRecord["mode"] })}
              />
            </label>
            <label className="rt-ed__f">
              <span>Transit days</span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                placeholder="—"
                value={transitDays ?? ""}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === "") {
                    setDraft({ ...draft, transit: "—" });
                    return;
                  }
                  const n = Number(raw);
                  setDraft({
                    ...draft,
                    transit: Number.isFinite(n) && n >= 1 ? formatTransitLabel(n) : raw,
                  });
                }}
              />
              {transitInvalid && <em className="rt-ed__err">Transit must be at least 1 day.</em>}
            </label>
            <label className="rt-ed__f">
              <span>Liner terms</span>
              <Select
                ariaLabel="Liner terms"
                value={linerChoice}
                options={LINER_TERM_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                onChange={(value) => setLinerTerm(value as LinerTermChoice)}
              />
            </label>
            <div className="rt-ed__f">
              <span>Validity</span>
              <ValidityDateRangeFields
                from={validityRange.from}
                until={validityRange.until}
                onChange={setValidityDates}
                size="md"
              />
              {invalidValidityRange && <em className="rt-ed__err">Until must be on or after From.</em>}
            </div>
            <label className="rt-ed__f">
              <span>Transshipment</span>
              <input
                value={draft.transshipment}
                placeholder="Comma-separated ports"
                onChange={(event) => setDraft({ ...draft, transshipment: event.target.value })}
              />
            </label>
          </div>
          <div className="rt-ed__acts">
            <button type="button" className="pcbtn pcbtn--main" disabled={saveDisabled} onClick={save}>
              Save
            </button>
            <button type="button" className="pcbtn" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </article>
  );
};
